// League workspace — Finances view.
//
// Top-level shell: page header + season picker + sub-tab strip.
// The four sub-tabs mirror the 2026_Balance_Sheet workbook:
//   1. Balance Sheet — dashboard with KPIs, income/expense summary,
//      cash-on-hand cards
//   2. Team Fees     — payment ledger grouped by category
//   3. Umpiring      — payment ledger + rate schedule manager
//   4. Expenses      — categorised expense ledger
//
// Season picker at the top switches every child tab's scope. Writes
// are gated to ADMIN / PRESIDENT / TREASURER; everyone else can read.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  DollarSign, Plus, X, Calendar, ChevronDown, Check
} from "lucide-react";
import { Panel } from "../../components/primitives";
import { useToast } from "../../components/Toast";
import { api } from "../../api";
import { FIN_TABS, canWriteFinance } from "./finance/shared";

import FinanceOverview  from "./finance/FinanceOverview";
import FinanceTeamFees  from "./finance/FinanceTeamFees";
import FinanceUmpiring  from "./finance/FinanceUmpiring";
import FinanceExpenses  from "./finance/FinanceExpenses";

function currentYear() {
  try {
    return Number(new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", year: "numeric"
    }).format(new Date()));
  } catch { return new Date().getFullYear(); }
}

export default function LeagueFinances({ user }) {
  const toast = useToast();
  const [seasons,      setSeasons]      = useState([]);
  const [seasonId,     setSeasonId]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [tab,          setTab]          = useState("overview");
  const [showNewSeason, setShowNewSeason] = useState(false);
  const canWrite = canWriteFinance(user);

  async function refreshSeasons(preferId = null) {
    setLoading(true);
    try {
      const list = await api.listFinanceSeasons();
      setSeasons(list);
      if (list.length === 0) {
        setSeasonId(null);
      } else {
        // Prefer the requested id → active → newest.
        const nextId = preferId && list.find(s => s.id === preferId)
          ? preferId
          : (list.find(s => s.isActive)?.id || list[0].id);
        setSeasonId(nextId);
      }
    } catch (err) {
      toast.error(err.message || "Couldn't load finance seasons.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refreshSeasons(); /* eslint-disable-next-line */ }, []);

  async function createDefaultSeason() {
    const year = currentYear();
    try {
      const { id } = await api.createFinanceSeason({ year, label: `Season-${year}` });
      await api.activateFinanceSeason(id);
      toast.success(`Season-${year} created and set as active.`);
      await refreshSeasons(id);
    } catch (err) { toast.error(err.message || "Couldn't create season."); }
  }

  const activeSeason = seasons.find(s => s.id === seasonId) || null;
  const props = {
    user,
    seasonId,
    activeSeason,
    seasons,
    canWrite,
    refresh: () => refreshSeasons(seasonId)
  };

  return (
    <div className="stack league-view">
      <div className="builderTop">
        <div>
          <p>Pittsburgh Cricket League</p>
          <h2>Finances</h2>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <SeasonPicker
            seasons={seasons}
            seasonId={seasonId}
            onChange={setSeasonId}
            canWrite={canWrite}
            onOpenNew={() => setShowNewSeason(true)}
          />
        </div>
      </div>

      {loading && <p style={{color:"var(--muted)"}}>Loading seasons…</p>}

      {!loading && seasons.length === 0 && (
        <Panel title={<><DollarSign size={18}/> Start your first finance season</>}>
          <div className="league-annEmpty">
            <DollarSign size={30}/>
            <p>
              Finances are grouped by season so past years live side-by-side.
              {canWrite
                ? " Create one to begin logging income and expenses."
                : " Ask an admin or treasurer to create the first one."}
            </p>
            {canWrite && (
              <button className="btn" onClick={createDefaultSeason} style={{marginTop:10}}>
                <Plus size={16}/> Create Season-{currentYear()}
              </button>
            )}
          </div>
        </Panel>
      )}

      {!loading && seasons.length > 0 && (
        <>
          {/* Sub-tab strip */}
          <div className="league-subTabs" role="tablist" aria-label="Finance sections">
            {FIN_TABS.map(({ key, label, Icon, icon: I }) => {
              const IconC = Icon || I;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={`league-subTab${tab === key ? " league-subTab--active" : ""}`}
                  onClick={() => setTab(key)}
                >
                  <IconC size={13}/> {label}
                </button>
              );
            })}
          </div>

          {tab === "overview" && <FinanceOverview {...props}/>}
          {tab === "teamFees" && <FinanceTeamFees {...props}/>}
          {tab === "umpiring" && <FinanceUmpiring {...props}/>}
          {tab === "expenses" && <FinanceExpenses {...props}/>}
        </>
      )}

      {showNewSeason && (
        <NewSeasonModal
          onClose={() => setShowNewSeason(false)}
          onCreated={id => { setShowNewSeason(false); refreshSeasons(id); }}
          existing={seasons}
        />
      )}
    </div>
  );
}

// ─── Season picker button + dropdown ─────────────────────────────
function SeasonPicker({ seasons, seasonId, onChange, canWrite, onOpenNew }) {
  const [open, setOpen] = useState(false);
  const current = seasons.find(s => s.id === seasonId);

  useEffect(() => {
    if (!open) return;
    const onClick = e => {
      if (!e.target.closest?.(".fin-season")) setOpen(false);
    };
    const onKey = e => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="fin-season">
      <button
        type="button"
        className="fin-season-btn"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Calendar size={14}/>
        <span className="fin-season-btn-label">
          <small>Season</small>
          <b>{current ? current.label : "—"}</b>
        </span>
        <ChevronDown size={14}/>
      </button>
      {open && (
        <div className="fin-season-menu" role="menu">
          {seasons.map(s => (
            <button
              key={s.id}
              type="button"
              role="menuitemradio"
              aria-checked={s.id === seasonId}
              className={`fin-season-item${s.id === seasonId ? " fin-season-item--active" : ""}`}
              onClick={() => { onChange(s.id); setOpen(false); }}
            >
              <span className="fin-season-item-name">
                {s.label}
                {s.isActive && <em className="fin-season-item-active">Active</em>}
              </span>
              <span className="fin-season-item-year">{s.year}</span>
              {s.id === seasonId && <Check size={13}/>}
            </button>
          ))}
          {canWrite && (
            <>
              <div className="fin-season-sep" role="separator"/>
              <button
                type="button"
                role="menuitem"
                className="fin-season-item fin-season-item--new"
                onClick={() => { setOpen(false); onOpenNew(); }}
              >
                <Plus size={13}/>
                <span>New season…</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── New-season modal (year + label) ─────────────────────────────
function NewSeasonModal({ onClose, onCreated, existing }) {
  const toast = useToast();
  const [year,  setYear]  = useState(currentYear() + 1);
  const [label, setLabel] = useState(`Season-${currentYear() + 1}`);
  const [busy,  setBusy]  = useState(false);
  const [activate, setActivate] = useState(true);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => { setLabel(`Season-${year}`); }, [year]);

  async function submit(e) {
    e.preventDefault();
    if (!Number.isFinite(Number(year))) { toast.error("Year required."); return; }
    if (!label.trim())                  { toast.error("Label required."); return; }
    if (existing.some(s => s.year === Number(year))) {
      toast.error("A season for that year already exists.");
      return;
    }
    setBusy(true);
    try {
      const { id } = await api.createFinanceSeason({ year: Number(year), label: label.trim() });
      if (activate) await api.activateFinanceSeason(id);
      toast.success("Season created.");
      onCreated(id);
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally { setBusy(false); }
  }

  return createPortal((
    <div className="modal" onClick={onClose}>
      <div className="modalInner" onClick={e => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose} aria-label="Close">
          <X size={20}/>
        </button>
        <Panel title="New Finance Season">
          <form className="form" onSubmit={submit}>
            <div className="two">
              <div>
                <label>Year *</label>
                <input type="number" min="2000" max="2100"
                  value={year}
                  onChange={e => setYear(e.target.value)}/>
              </div>
              <div>
                <label>Label *</label>
                <input value={label} onChange={e => setLabel(e.target.value)}
                  placeholder="Season-2027"/>
              </div>
            </div>

            <label style={{display:"inline-flex", alignItems:"center", gap:8, textTransform:"none", letterSpacing:0, color:"var(--text)"}}>
              <input type="checkbox" checked={activate}
                onChange={e => setActivate(e.target.checked)}
                style={{width:"auto"}}/>
              Set as active season
            </label>

            <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:8}}>
              <button type="button" className="dark" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="btn" disabled={busy}>
                {busy ? "Saving…" : "Create Season"}
              </button>
            </div>
          </form>
        </Panel>
      </div>
    </div>
  ), document.body);
}
